import React, { useState, useCallback, useEffect } from 'react';
import { imagePlugin, type ImageUploadHandler } from '@mdxeditor/editor';
import ImageUploader from '../../ImageUploader/ImageUploader';

// Global state to manage the image uploader dialog
let imageUploaderCallback: ((url: string) => void) | null = null;
let openImageUploader: (() => void) | null = null;

// Custom component to wrap the MDXEditor and provide the image uploader
export const MDXEditorImageUploader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Set the global openImageUploader function
  useEffect(() => {
    openImageUploader = () => setIsOpen(true);
    return () => {
      openImageUploader = null;
    };
  }, []);

  const handleSelectImage = useCallback((imageUrl: string) => {
    if (imageUploaderCallback) {
      imageUploaderCallback(imageUrl);
      imageUploaderCallback = null;
    }
    setIsOpen(false);
  }, []);

  return (
    <>
      {children}
      <ImageUploader
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSelectImage={handleSelectImage}
      />
    </>
  );
};

// Define the interface for image plugin parameters
interface ImagePluginParams {
  imageUploadHandler?: ImageUploadHandler;
  imageAutocompleteSuggestions?: string[];
  disableImageResize?: boolean;
  disableImageSettingsButton?: boolean;
  imagePreviewHandler?: ((imageSource: string) => Promise<string>) | null;
  ImageDialog?: React.FC<{}> | (() => JSX.Element);
  EditImageToolbar?: React.FC<{}> | (() => JSX.Element);
  imagePlaceholder?: (() => JSX.Element) | null;
}

// Custom image plugin that uses our image uploader
export const customImagePlugin = (params?: Partial<ImagePluginParams>) => {
  const customParams: Partial<ImagePluginParams> = {
    ...params,
    imageUploadHandler: (image: File | string) => {
      // If image is a string (URL), use it directly
      if (typeof image === 'string') {
        // We can't pass a string to the original imageUploadHandler as it expects a File
        // So we just resolve with the string directly
        return Promise.resolve(image);
      }

      // If image is a File, open the image uploader
      return new Promise<string>((resolve) => {
        // Store the callback to resolve the promise when an image is selected
        imageUploaderCallback = resolve;

        // Open the image uploader dialog
        if (openImageUploader) {
          openImageUploader();
        }
      });
    }
  };

  // Return the default imagePlugin with our custom parameters
  return imagePlugin(customParams);
};

export default { customImagePlugin, MDXEditorImageUploader };
